import { Clipboard, Clock, Download, Pencil, Trash, Trash2, Reply, Check, Smile, Eye, Pin, PinOff, Star } from "lucide-react";
import { t } from "i18next";
import { User } from "../../global-components/src/types";
import { getImagePathBucketPacoca } from "../../global-components/src/utils/utils";
import { MessageChat, ImagePost } from "../../types";
import { clearText, extractYouTubeId, } from "../../global-components/src/utils/text";
import { renderTextWithPhoneMessageLinks } from '../../utils/phone-message-links';
import { detectLanguage, isUserLang, translate } from "../../global-components/src/utils/language";
import { YoutubePreview } from "../../components/Post/Post/youtube-preview";
import { useAuth, useMessage } from "../../global-components/src";
import { useEffect, useRef, useState } from "react";
import ImageModal from "../../components/Post/ImgPost/image-modal";
import hljs from "highlight.js";
import { BUCKET_PACOCA_URL, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import axios from "axios";
import { api } from "../../services/api";
import ChatAudioMessage from '../../components/chat-audio-message';
import { PostLinkPreview } from "../../components/Post/Post/post-link-preview";
import { ChatReactionPicker } from "./chat-reaction-picker";
import { ChatReactionsModal } from "./chat-reactions-modal";
import { ReactionEmoji } from "./reaction-emoji";
import "./chat-reactions.css";
import { containsMarkdownCode } from "../../utils/markdown-code";
import ChatImageGallery from "./chat-image-gallery";
import ChatImageGrid from "./chat-image-grid";

interface MessageProps {
    m: MessageChat;
    user: User | null;
    isOwnMessage: boolean;
    onPressStart: any;
    onPressEnd: any;
    onPressMove: any;
    selectedMessages: any;
    chatSelected: any;
    setSelectedMessages: any;
    types: any;
    paths: any;
    closeDropdown: any;
    edit: any;
    isTextLong: any;
    isExpanded: any;
    toggleExpand: any;
    formatarData: any;
    names: any;
    openImage: any;
    setOpenImage: any;
    setShowConfirmationDeleteMessage: any;
    setShowConfirmationDeleteMessageForAll: any;
    setReplyingTo: (m: MessageChat) => void;
    highlightedMessageId?: number | null;
    scrollToMessage: (id: number) => void;
    onReactMessage?: (messageId: number, emoji: string) => void;
    onMessageUpdated?: (updatedMessage: MessageChat) => void;
    onPinMessage?: (m: MessageChat) => void;
    onFavoriteMessage?: (m: MessageChat) => void;
    canPin?: boolean;
    showSender: boolean;
}

// Threshold de pixels para disparar o reply ao arrastar
const SWIPE_THRESHOLD = 60;
const AUDIO_FILE_EXTENSION = /\.(aac|flac|m4a|mp3|oga|ogg|opus|wav|weba|webm)(?:[?#].*)?$/i;
const RECORDED_VOICE_NAME = /^audio\.(mp4|m4a|aac|webm|ogg|opus|mp3|wav|3gp)$/i;

const isAudioAttachment = (type?: string, path?: string, name?: string) => {
    const normalizedType = (type ?? '').toLowerCase().split(';', 1)[0].trim();
    const normalizedPath = (path ?? '').split(/[?#]/, 1)[0];

    return (
        normalizedType === 'audio' ||
        normalizedType.startsWith('audio/') ||
        normalizedType.startsWith('video/webm') ||
        RECORDED_VOICE_NAME.test((name ?? '').trim()) ||
        AUDIO_FILE_EXTENSION.test(name ?? '') ||
        AUDIO_FILE_EXTENSION.test(normalizedPath)
    );
};

const getAvatarPath = (path?: string) => {
    if (!path) return undefined;
    if (/^(https?:|blob:|data:|\/)/i.test(path)) return path;
    return getImagePathBucketPacoca(path, '/img/img-account.png');
};

export const MessageChatComponent = ({ m, onPressStart, onPressEnd, onPressMove, selectedMessages, chatSelected, user, setSelectedMessages, types, paths, closeDropdown, edit, isExpanded, toggleExpand, formatarData, names, openImage, setOpenImage, setShowConfirmationDeleteMessage, setShowConfirmationDeleteMessageForAll, setReplyingTo, highlightedMessageId, scrollToMessage, onReactMessage, onMessageUpdated, onPinMessage, onFavoriteMessage, canPin, showSender }: MessageProps) => {
    const textRef = useRef<HTMLParagraphElement>(null);
    const [modalImgIndex, setModalImgIndex] = useState<number | null>(null);
    const [showAllImages, setShowAllImages] = useState(false);
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const [showReactionsModal, setShowReactionsModal] = useState(false);

    useEffect(() => {
        if (!m.is_view_once || modalImgIndex === null) return;
        const postNativeMessage = window.ReactNativeWebView?.postMessage;
        if (!postNativeMessage) return;

        postNativeMessage(JSON.stringify({
            type: "screenCaptureProtection",
            enabled: true,
        }));

        return () => {
            postNativeMessage(JSON.stringify({
                type: "screenCaptureProtection",
                enabled: false,
            }));
        };
    }, [m.is_view_once, modalImgIndex]);

    const [processedText, setProcessedText] = useState<string>(m.text ?? "");
    const [oldProcessedText, setOldProcessedText] = useState<string>(m.text ?? "");
    const [translatedText, setTranslatedText] = useState<string>("");
    const [isTranslate, setIsTranslate] = useState<boolean>(false);
    const [canTranslate, setCanTranslate] = useState<boolean>(false);
    const language = detectLanguage(m?.text);
    const isTextUserLang = isUserLang(language);
    const hasCode = containsMarkdownCode(m?.text);

    useEffect(() => {
        setProcessedText(m.text ?? "");
        setOldProcessedText(m.text ?? "");
        setIsTranslate(false);
        setTranslatedText("");
        setCanTranslate(false);
    }, [m.text]);

    useEffect(() => {
        if (!hasCode && !isTextUserLang && !m.gif_url && m.text && m.text.trim().length >= 5) {
            translateText();
        }
    }, [m.text, isTextUserLang, m.gif_url, hasCode]);

    const translateText = async (updateText = false) => {
        if (hasCode) return;

        if (translatedText.length && updateText) {
            setProcessedText(translatedText);
            setIsTranslate(true);
            return;
        }

        try {
            let translated = await translate(m?.text);
            if (!translated || m?.text?.trim() === translated.trim()) return;
            translated = renderTextWithPhoneMessageLinks(translated, clearText);
            setTranslatedText(translated);

            if (updateText) {
                setProcessedText(translated);
                setIsTranslate(true);
            }
            setCanTranslate(true);
        } catch (err: any) {
            console.error('Translation error:', err?.response?.data || err);
            return null;
        }
    };

    const showOriginal = () => {
        setProcessedText(oldProcessedText);
        setIsTranslate(false);
    };

    // ── Swipe-to-reply state ──────────────────────────────────────────────────
    const swipeStartX = useRef<number | null>(null);
    const swipeStartY = useRef<number | null>(null);
    const [swipeDeltaX, setSwipeDeltaX] = useState(0);
    const swipeFired = useRef(false);
    const isOwnMessage = m.user_id === user?.id;

    // double-click no PC → reply (só para PC, tela > 850px)
    const handleDoubleClick = () => {
        if (window.innerWidth <= 850) return;
        if (selectedMessages.length > 0) return;
        setReplyingTo(m);
    };

    const swipeHandlers = {
        onTouchStart: (e: React.TouchEvent) => {
            swipeStartX.current = e.touches[0].clientX;
            swipeStartY.current = e.touches[0].clientY;
            swipeFired.current = false;
        },
        onTouchMove: (e: React.TouchEvent) => {
            if (swipeStartX.current === null || swipeStartY.current === null) return;
            if (selectedMessages.length > 0) return;

            const dx = e.touches[0].clientX - swipeStartX.current;
            const dy = Math.abs(e.touches[0].clientY - swipeStartY.current);

            // cancela se o movimento for mais vertical que horizontal
            if (dy > Math.abs(dx) * 1.2) return;

            // ambas as mensagens arrastam para a direita (dx > 0)
            const relevant = Math.max(0, dx);
            const clamped = Math.min(SWIPE_THRESHOLD + 10, relevant);

            setSwipeDeltaX(clamped);

            if (!swipeFired.current && Math.abs(clamped) >= SWIPE_THRESHOLD) {
                swipeFired.current = true;
                setReplyingTo(m);
                if (navigator.vibrate) navigator.vibrate(40);
            }
        },
        onTouchEnd: () => {
            swipeStartX.current = null;
            swipeStartY.current = null;
            setSwipeDeltaX(0);
        },
    };

    const { setMessage } = useMessage();
    const copyText = (text: string) => {
        navigator.clipboard.writeText(text);
        setMessage(t("chat.textCopied"), "success");
        setSelectedMessages([]);
    };

    const copyMediaLink = async (url: string) => {
        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                return true;
            } catch {
                // Tenta o fallback para navegadores sem acesso à Clipboard API.
            }
        }

        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();

        try {
            return document.execCommand("copy");
        } finally {
            textarea.remove();
        }
    };

    const shareMedia = async (url: string) => {
        if (!url) return;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: t("post.shareVia"),
                    text: m.text?.trim() || undefined,
                    url,
                });
                return;
            } catch (error) {
                if (error instanceof DOMException && error.name === "AbortError") return;
            }
        }

        try {
            const copied = await copyMediaLink(url);
            if (!copied) throw new Error("Não foi possível copiar o link da mídia");
            setMessage(t("post.linkCopied"), "success");
        } catch {
            setMessage("Não foi possível compartilhar a mídia.", "error");
        }
    };

    const getImagePath = (path: string) => {
        if(path.includes("http://") || path.includes("https://") || path.includes("blob"))
            return path;
    
        return BUCKET_PACOCA_URL + "/" + path;
    };

    // Build the image list once so both the message grid and the full-screen
    // viewers keep the same order, even when a message also has other files.
    const chatImages: ImagePost[] = paths
        .map((path: string, i: number) => ({ id: i, path: getImagePath(path), type: 0, id_post: m.id }))
        .filter((_: ImagePost, i: number) => types[i]?.startsWith('image'));
    const firstImagePathIndex = types.findIndex((type: string) => type?.startsWith('image'));

    const isTextLong = (text: string) => {
        const maxChars = 300;
        const maxLines = 20;
        const lines = text.split('\n').length;
        return text.length > ( maxChars + 200) || lines > maxLines;
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            hljs.highlightAll();
        }, 20);
        return () => clearTimeout(timeout);
    }, [m.text]);

    useEffect(() => {
        // if(m.text.includes('language')){
             const interval = setInterval(() => {
                 hljs.highlightAll();
    
                if (textRef.current) {
                    const realHeight = textRef.current.scrollHeight;
                    const maxHeight = 200;
                    const onlyEmoji = m.text.trim().match(/^<img[^>]*class=['"]emoji['"][^>]*>$/);
                    // setIsExpanded(realHeight > maxHeight + 20 && !onlyEmoji);
                }
            }, 20); // roda a cada 20ms
    
            return () => clearInterval(interval); // limpa ao desmontar
        // }
        
    }, [m.text, isExpanded]);

    const swipeProgress = Math.min(1, Math.abs(swipeDeltaX) / SWIPE_THRESHOLD);

    // image-only: no text, no gif, every file is an image
    const isImageOnly =
        !m.is_view_once &&
        !m.text?.trim() &&
        !m.gif_url &&
        paths.length > 0 &&
        types.every((t: string) => t?.startsWith('image'));

    const isSingleAudioOnly =
        !m.is_view_once &&
        paths.length === 1 &&
        !m.text?.trim() &&
        !m.gif_url &&
        isAudioAttachment(types[0], paths[0], names[0]);

    const isViewOnceOpened = Boolean(m.is_view_once && m.view_once_opened_at);

    const toggleSelectionFromMedia = () => {
        if (selectedMessages.length === 0) return false;
        setSelectedMessages((previous: number[]) =>
            previous.includes(m.id)
                ? previous.filter(messageId => messageId !== m.id)
                : [...previous, m.id]
        );
        return true;
    };

    const handleOpenViewOnce = () => {
        if (toggleSelectionFromMedia() || isViewOnceOpened) return;
        setModalImgIndex(0);
    };

    const { token: authToken } = useAuth();

    const handleCloseModal = async () => {
        setModalImgIndex(null);
        if (m.is_view_once && !m.view_once_opened_at) {
            const now = new Date().toISOString();
            const updated = { ...m, view_once_opened_at: now };
            if (onMessageUpdated) {
                onMessageUpdated(updated);
            }
            try {
                await api(authToken).post(`/chats/messages/${m.id}/open-view-once`);
            } catch (e) {
                console.error("Erro ao registrar visualização única:", e);
            }
        }
    };

    return (
        <>
        <div
            id={`msg-${m.id}`}
            style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: isOwnMessage ? "flex-end" : "flex-start" }}
            className={highlightedMessageId === m.id ? "msg-highlighted" : ""}
        >

            {/* Ícone de reply que aparece atrás da bolha durante o swipe */}
            {selectedMessages.length <= 0 && swipeProgress > 0 && (
                <div
                    className="swipe-reply-icon"
                    style={{
                        opacity: swipeProgress,
                        transform: `scale(${0.5 + swipeProgress * 0.5})`,
                        // ambas arrastam para direita → ícone aparece sempre à esquerda
                        left: 8,
                    }}
                >
                    <Reply size={20} />
                </div>
            )}

            <div
                // key={index}
                className={`background-click-area cursor-pointer div-text-chat ${m.user_id == user?.id ? "div-text-chat-logado" : ""} ${selectedMessages.includes(m.id) ? "selected-message user-select-none" : ""}`}
                style={{
                    transform: swipeDeltaX !== 0 ? `translateX(${swipeDeltaX * 0.45}px)` : undefined,
                    transition: swipeDeltaX !== 0 ? "none" : "transform 0.2s ease",
                }}
                onDoubleClick={handleDoubleClick}
                onMouseDown={(e) => onPressStart(m.id, "mouse", e)}
                onMouseUp={(e) => onPressEnd(m.id, "mouse", e)}
                onMouseMove={(e) => onPressMove(e, "mouse")}
                onTouchStart={(e) => { swipeHandlers.onTouchStart(e); onPressStart(m.id, "touch", e); }}
                onTouchEnd={(e) => { swipeHandlers.onTouchEnd(); onPressEnd(m.id, "touch", e); }}
                onTouchMove={(e) => { swipeHandlers.onTouchMove(e); onPressMove(e, "touch"); }}
            >
                {(selectedMessages.length > 0 && window.innerWidth > 850) || selectedMessages.length > 1 && (
                    <div
                        className={`msg-select-checkbox ${selectedMessages.includes(m.id) ? "checked" : ""}`}
                        onClick={() => onPressEnd(m.id, "mouse", { changedTouches: [{clientX:0, clientY:0}] } as any)}
                    >
                        <Check/>
                    </div>
                )}

                {String(m.user_id) != chatSelected.user_id && m.user_id != user?.id && (
                    <span
                        className="chat-message-sender-slot"
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                    >
                        {showSender && (
                            <img className="user-select-none img-account-message" src={getImagePathBucketPacoca(m.user?.img_account)} onError={(e) => { e.currentTarget.src = "/img/img-account.png"; }} alt=""/>
                        )}
                    </span>
                )}
                
                <div onMouseDown={(e) => e.stopPropagation()} style={{ overflowWrap: "anywhere", position: "relative" }} className={`${m.user_id == user?.id ? "text-user-logado" : "text-user-nao-logado"}${isImageOnly ? " chat-img-only" : ""}`}>
                {!selectedMessages.length ? (
                    <div
                    className={`dropdown message-actions ${m.user_id == user?.id ? "message-actions-logado" : "message-actions-nao-logado"}`}>
                        <button
                            className="btn btn-sm dropdown-toggle"
                            type="button"
                            id={`dropdownMenuButton-${m.id}`}
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                        </button>

                        
                            <ul className="dropdown-menu" style={{display: "flex", top: 6, right: 3, height: 35, padding: 0, paddingLeft: 5, paddingRight: 5, borderRadius: 15, minWidth: "unset", width: "auto", justifyContent: "center", alignItems: "center" }} aria-labelledby={`dropdownMenuButton-${m.id}`}>
                                    <li>
                                        <button style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35 }} className="dropdown-item" title="Reagir" onClick={() => { closeDropdown(); setShowReactionPicker(!showReactionPicker); }}>
                                            <Smile size={16} style={{marginRight: "0px"}}/>
                                        </button>
                                    </li>
                                    <li>
                                        <button style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35 }} className="dropdown-item" title="Responder" onClick={() => { closeDropdown(); setReplyingTo(m); }}>
                                            <Reply size={16} style={{marginRight: "0px"}}/>
                                        </button>
                                    </li>
                                    {m.text.trim().length ? (
                                        <li>
                                            <button style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35 }} className="dropdown-item" onClick={() => copyText(m.text)}>
                                                <Clipboard size={16} style={{marginRight: "0px"}}/>
                                            </button>
                                        </li>
                                    ) : null}

                                    {/* Favorite */}
                                    {onFavoriteMessage && (
                                        <li>
                                            <button
                                                style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35}}
                                                className="dropdown-item"
                                                title={m.is_favorited ? t("chat.unfavorite") : t("chat.favorite")}
                                                onClick={() => { closeDropdown(); onFavoriteMessage(m); }}
                                            >
                                                <Star size={16} style={{marginRight: "0px", fill: m.is_favorited ? "currentColor" : "none"}}/>
                                            </button>
                                        </li>
                                    )}

                                    {/* Pin / Unpin */}
                                    {canPin && onPinMessage && (
                                        <li>
                                            <button
                                                style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35}}
                                                className="dropdown-item"
                                                title={m.is_pinned ? t("chat.unpin") : t("chat.pin")}
                                                onClick={() => { closeDropdown(); onPinMessage(m); }}
                                            >
                                                {m.is_pinned ? <PinOff size={16} style={{marginRight: "0px"}}/> : <Pin size={16} style={{marginRight: "0px"}}/>}
                                            </button>
                                        </li>
                                    )}
                                
                                <li>
                                    {/* // 5 minutos */}
                                    {m.user_id == user?.id && Date.now() - new Date(m.created_at).getTime() < 5 * 60 * 1000  ? (
                                        <button style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35 }} className="dropdown-item" onClick={() => {closeDropdown(); edit(m)}}>
                                            <Pencil size={16}/>
                                            {/* Editar */}
                                        </button>
                                    ) : null}
                                </li>

                                <li>
                                    <button style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35 }} className="dropdown-item" onClick={() => {closeDropdown(); setSelectedMessages([m.id]); setShowConfirmationDeleteMessage(true); }}>
                                        <Trash size={16}/>
                                        {/* Apagar  */}
                                        {/* {m.user_id == user?.id ? "para mim" : ""} */}
                                    </button>
                                </li>

                                {m.user_id == user?.id ? (
                                    <li>
                                        <button style={{display: "flex", justifyContent: "center", alignItems: "center", padding: 0, width: 30, height: 35 }} className="dropdown-item" onClick={() => {closeDropdown(); setSelectedMessages([m.id]); setShowConfirmationDeleteMessageForAll(true); }}>
                                            <Trash2 size={16}/>
                                            {/* Apagar para todos */}
                                        </button>
                                    </li>
                                ) : null}
                            </ul>
                    </div>
                    ) : null}
                    {showSender && String(m.user_id) != chatSelected.user_id && m.user_id != user?.id && (
                        <span onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <strong className="user-name-chat-message">{m.user?.name}</strong>
                        </span>
                    )}
                                                        
                    {m.reply_to && (
                        <div
                            className={`reply-bubble ${m.user_id == user?.id ? "reply-bubble-me" : "reply-bubble-other"}`}
                            style={{ cursor: "pointer" }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => scrollToMessage(m.reply_to!.id)}
                        >
                            <span className="reply-bubble-name">
                                {m.reply_to.user?.name ?? (m.reply_to.user_id === user?.id ? t("chat.you") : t("chat.typeMessage"))}
                            </span>
                            <span className="reply-bubble-text">
                                {m.reply_to.is_view_once
                                    ? "Visualização única"
                                    : m.reply_to.gif_url
                                        ? "🖼️ GIF"
                                        : m.reply_to.file_path && JSON.parse(m.reply_to.file_path).length > 0
                                            ? (() => {
                                                const mime = JSON.parse(m.reply_to.file_type ?? "[]")[0] ?? "";
                                                if (mime.startsWith("image")) return `📷 ${t("chat.message.image")}`;
                                                if (mime.startsWith("video")) return `🎥 ${t("chat.message.video")}`;
                                                if (mime.startsWith("audio")) return `🎤 ${t("chat.message.audio")}`;
                                                return `📎 ${t("chat.message.file")}`;
                                            })()
                                            : m.reply_to.text.slice(0, 100)}
                            </span>
                        </div>
                    )}

                    {m.text && !m.gif_url && (
                        <div
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <div className="message-text" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                    {isExpanded(m.id) || !isTextLong(processedText) ? (
                                        <>
                                        <p ref={textRef} dangerouslySetInnerHTML={{ __html: renderTextWithPhoneMessageLinks(processedText.replace(/\r/g, ""), clearText) }} />
                                        {isTextLong(processedText) && (
                                            <>
                                            {' '}
                                            <a
                                                href="#"
                                                className={`link-see-more-chat ${m.user_id == user?.id ? "me" : "other"}`}
                                                onClick={(e) => {
                                                e.preventDefault();
                                                toggleExpand(m.id);
                                                }}
                                                style={{ textDecoration: 'underline', marginLeft: 4 }}
                                            >
                                                Ver menos
                                            </a>
                                            </>
                                        )}
                                        </>
                                    ) : (
                                        <>
                                        <p ref={textRef} dangerouslySetInnerHTML={{ __html: renderTextWithPhoneMessageLinks(processedText.replace(/\r/g, "").slice(0, 300), clearText) }} />
                                        {'... '}
                                        <a
                                            href="#"
                                            className={`link-see-more-chat ${m.user_id == user?.id ? "me" : "other"}`}
                                            onClick={(e) => {
                                            e.preventDefault();
                                            toggleExpand(m.id);
                                            }}
                                            style={{ textDecoration: 'underline' }}
                                        >
                                            Ver mais
                                        </a>
                                        </>
                                    )}
                                </div>

                                {!hasCode && !isTextUserLang && canTranslate && !isTranslate && (
                                    <span className='user-select-none text-translate-post' style={{ fontSize: '12px', cursor: 'pointer', fontWeight: 600, display: 'inline-block', marginTop: 4, textDecoration: 'underline' }}>
                                        <span onClick={() => translateText(true)}>{t("post.actions.translate")}</span>
                                    </span>
                                )}
                                {!hasCode && !isTextUserLang && canTranslate && isTranslate && (
                                    <span className='user-select-none text-translate-post' style={{ fontSize: '12px', cursor: 'pointer', fontWeight: 600, display: 'inline-block', marginTop: 4, textDecoration: 'underline' }}>
                                        <span onClick={showOriginal}>{t("post.actions.untranslate")}</span>
                                    </span>
                                )}

                        </div>
                    )}

                    {/* YOUTUBE TEM PRIORIDADE */}
                    {Boolean(extractYouTubeId(m.text)) && !m.gif_url && (
                        <YoutubePreview text={m.text} />
                    )}
        
                    {/* LINK PREVIEW SÓ SE NÃO FOR YOUTUBE */}
                    {Boolean(!extractYouTubeId(m.text)) && !m.gif_url && (
                        <PostLinkPreview text={m.text} className="chat-link-preview" />
                    )}


                    {/* Arquivos de áudio */}
                <div className="div-files">
                    {m.gif_url && (
                        <img
                            src={m.gif_url}
                            className="comment-gif"
                            alt="GIF"
                        />
                    )}

                    {m.is_view_once ? (
                        <div
                            className={`view-once-bubble ${isViewOnceOpened ? "opened" : "unopened"}`}
                            onClick={handleOpenViewOnce}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="view-once-icon-wrapper">
                                <span className="view-once-badge">1</span>
                            </div>
                            <span className="view-once-label">
                                {isViewOnceOpened ? "Mensagem aberta" : "Visualização única"}
                            </span>
                        </div>
                    ) : isImageOnly ? (
                        <ChatImageGrid
                            images={chatImages}
                            onImagePress={index => { if (toggleSelectionFromMedia()) return; setModalImgIndex(index); }}
                            onOpenAll={() => { if (toggleSelectionFromMedia()) return; setShowAllImages(true); }}
                            isAlone={chatImages.length === 1}
                            showMetadata
                            timeText={formatarData(m.created_at)}
                            isPinned={m.is_pinned}
                            isFavorited={m.is_favorited}
                            idNotSend={m.idNotSend}
                        />
                    ) : (
                    paths.map((path: string, i: number) => {
                        const type = types[i];
                        if (type?.startsWith('image')) {
                            if (i !== firstImagePathIndex) return null;
                            return (
                                <ChatImageGrid
                                    key={`image-grid-${m.id}`}
                                    images={chatImages}
                                    onImagePress={index => { if (toggleSelectionFromMedia()) return; setModalImgIndex(index); }}
                                    onOpenAll={() => { if (toggleSelectionFromMedia()) return; setShowAllImages(true); }}
                                    isAlone={chatImages.length === 1 && !m.text?.trim()}
                                    timeText={formatarData(m.created_at)}
                                    isPinned={m.is_pinned}
                                    isFavorited={m.is_favorited}
                                    idNotSend={m.idNotSend}
                                />
                            );
                        }

                        if (isAudioAttachment(type, path, names[i])) {
                            const audioUser = m.user ?? (isOwnMessage ? user : null) ?? chatSelected;

                            return (
                                <ChatAudioMessage
                                    key={`${m.id}-${i}`}
                                    src={path && /^(https?:|blob:|data:)/i.test(path) ? path : (path ? getImagePath(path) : "")}
                                    avatarPath={getAvatarPath(audioUser?.img_account)}
                                    avatarName={audioUser?.name}
                                    isOwn={isOwnMessage}
                                    footerRight={isSingleAudioOnly ? (
                                        <span className="chat-audio-message-meta">
                                            {m.is_pinned && <Pin size={11} />}
                                            {m.is_favorited && <Star size={11} fill="currentColor" />}
                                            <span>{formatarData(m.created_at)}</span>
                                            {isOwnMessage && !m.idNotSend && (
                                                <span
                                                    className={`message-read-receipt ${m.is_read ? "message-read-receipt--read" : ""}`}
                                                    title={m.is_read ? "Lida" : "Enviada"}
                                                    aria-label={m.is_read ? "Mensagem lida" : "Mensagem enviada"}
                                                >
                                                    <Check size={12} />
                                                    <Check size={12} />
                                                </span>
                                            )}
                                        </span>
                                    ) : undefined}
                                />
                            );
                        }

                        if (type?.startsWith('video')) {
                            return <video className="preview-video" controls src={path && path.includes("blob:") ? path : (path ? getImagePath(path) : "")} />
                        }

                        return (
                            <div key={i} className="file-chat-wa">
                                <div className="file-chat-wa-icon">
                                    {type.endsWith("pdf") && <img className="file-chat-wa-img" src="/img/icon/pdf.png" alt="PDF" />}
                                    {type.endsWith("excel") && <img className="file-chat-wa-img" src="/img/icon/excel.png" alt="Excel" />}
                                    {type.includes("word") && <img className="file-chat-wa-img" src="/img/icon/word.png" alt="Word" />}
                                    {type.includes("audio") && <img className="file-chat-wa-img" src="/img/icon/audio.png" alt="Audio" />}
                                    {!type.endsWith("excel") && !type.includes("audio") && !type.includes("word") && !type.endsWith("pdf") && (
                                        <img className="file-chat-wa-img" src="/img/icon/file.png" alt="File" />
                                    )}
                                </div>
                                <div className="file-chat-wa-info">
                                    <span className="file-chat-wa-name">{names[i]}</span>
                                    <span className="file-chat-wa-ext">{type.split("/").pop()?.toUpperCase() ?? "FILE"}</span>
                                </div>
                                <a
                                    className="file-chat-wa-download"
                                    href={path && path.includes("blob:") ? path : (path ? getImagePath(path) : "#")}
                                    download={names[i]}
                                    title="Baixar arquivo"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Download size={18} />
                                </a>
                            </div>
                        )
                    })
                    )}
                </div>

                    {!isImageOnly && !isSingleAudioOnly && (
                        <p className="user-select-none data-mensagem" style={{ marginBottom: (m.reactions && m.reactions.length > 0) ? "6px" : undefined, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                            {m.is_pinned && <Pin size={12} />}
                            {m.is_favorited && <Star size={12} fill="currentColor" />}
                            <span>{formatarData(m.created_at)}</span>
                            {isOwnMessage && !m.idNotSend && (
                                <span
                                    className={`message-read-receipt ${m.is_read ? "message-read-receipt--read" : ""}`}
                                    title={m.is_read ? "Lida" : "Enviada"}
                                    aria-label={m.is_read ? "Mensagem lida" : "Mensagem enviada"}
                                >
                                    <Check size={13} />
                                    <Check size={13} />
                                </span>
                            )}
                        </p>
                    )}

                    {isImageOnly && isOwnMessage && !m.idNotSend && (
                        <span
                            className={`message-read-receipt message-read-receipt--media ${m.is_read ? "message-read-receipt--read" : ""}`}
                            title={m.is_read ? "Lida" : "Enviada"}
                            aria-label={m.is_read ? "Mensagem lida" : "Mensagem enviada"}
                        >
                            <Check size={13} />
                            <Check size={13} />
                        </span>
                    )}

                    {!isImageOnly && m.idNotSend ? (
                        <div className="d-flex w-100 div-status-comment" style={{ justifyContent: "right" }}>
                            <Clock className={`message-chat-father-icon ${m.errorSend ? "comment-father-red" : ""}`} />
                        </div>
                    ) : null}

                    {/* Reaction Badge (WhatsApp style bubble on bottom of message) */}
                    {m.reactions && m.reactions.length > 0 && (
                        <div
                            className={`message-reactions-badge ${isOwnMessage ? "message-reactions-badge-right" : "message-reactions-badge-left"}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowReactionsModal(true);
                            }}
                            title="Ver reações"
                        >
                            <span className="message-reactions-badge-emojis">
                                {Array.from(new Set(m.reactions.map((r) => r.emoji))).slice(0, 3).map((emoji, idx) => (
                                    <ReactionEmoji key={idx} emoji={emoji} size={15} />
                                ))}
                            </span>
                            {m.reactions.length > 1 && (
                                <span className="message-reactions-badge-count">{m.reactions.length}</span>
                            )}
                        </div>
                    )}
                </div>

            {/* {selectedMessages.length > 0 && isOwnMessage && (
                <div
                    className={`msg-select-checkbox ${selectedMessages.includes(m.id) ? "checked" : ""}`}
                    onClick={() => onPressEnd(m.id, "mouse", { changedTouches: [{clientX:0, clientY:0}] } as any)}
                >
                    <Check/>
                </div>
            )} */}
            </div>

            {/* Reaction Picker floating popover */}
            {((showReactionPicker) || (selectedMessages.length === 1 && selectedMessages[0] === m.id && window.innerWidth <= 850)) && onReactMessage && (
                <ChatReactionPicker
                    userReaction={m.reactions?.find((r) => r.user_id === user?.id)?.emoji}
                    position={window.innerWidth <= 850 ? "center" : "top"}
                    align={window.innerWidth <= 850 ? "center" : (isOwnMessage ? "right" : "left")}
                    onReact={(emoji) => {
                        setShowReactionPicker(false);
                        setSelectedMessages([]);
                        onReactMessage(m.id, emoji);
                    }}
                    onClose={() => {
                        setShowReactionPicker(false);
                        if (window.innerWidth <= 850 && selectedMessages.length === 1 && selectedMessages[0] === m.id) {
                            setSelectedMessages([]);
                        }
                    }}
                />
            )}
        </div>

        {modalImgIndex !== null && (!m.is_view_once || !m.view_once_opened_at) && (
            <ImageModal
                images={chatImages}
                initialIndex={modalImgIndex}
                getImagePath={(p) => p}
                open={true}
                onClose={handleCloseModal}
                onShare={shareMedia}
            />
        )}

        <ChatImageGallery
            open={showAllImages}
            images={chatImages}
            onClose={() => setShowAllImages(false)}
        />

        {showReactionsModal && m.reactions && (
            <ChatReactionsModal
                reactions={m.reactions}
                currentUserId={user?.id}
                onClose={() => setShowReactionsModal(false)}
                onRemoveReaction={
                    m.reactions.some((r) => r.user_id === user?.id) && onReactMessage
                        ? () => {
                            const myReaction = m.reactions?.find((r) => r.user_id === user?.id);
                            if (myReaction) onReactMessage(m.id, myReaction.emoji);
                        }
                        : undefined
                }
            />
        )}
        </>
    )
}